<template>
  <div class="msp">

    <!-- 顶部总览 -->
    <div class="msp-summary">
      <!-- 进度环 -->
      <div class="progress-ring-wrap">
        <svg class="progress-ring" viewBox="0 0 56 56">
          <circle cx="28" cy="28" r="24" class="ring-bg"/>
          <circle cx="28" cy="28" r="24" class="ring-fg"
            :stroke-dasharray="`${progressArc} ${150.796}`"
            :class="progressColor"
          />
        </svg>
        <div class="ring-label">
          <span class="ring-num">{{ signedCount }}</span>
          <span class="ring-total">/{{ total }}</span>
        </div>
      </div>

      <!-- 文字统计 -->
      <div class="summary-stats">
        <div class="stat-row">
          <span class="dot green"></span>
          <span class="stat-name">已签署</span>
          <span class="stat-val green">{{ signedCount }}</span>
        </div>
        <div class="stat-row">
          <span class="dot amber"></span>
          <span class="stat-name">签署中</span>
          <span class="stat-val amber">{{ signingCount }}</span>
        </div>
        <div class="stat-row">
          <span class="dot gray"></span>
          <span class="stat-name">待发起</span>
          <span class="stat-val gray">{{ pendingCount }}</span>
        </div>
      </div>

      <!-- 主操作 -->
      <div class="summary-action">
        <template v-if="config.sealMode === 'AUTHORIZED'">
          <el-button
            type="primary"
            @click="initiateAll"
            :loading="initiating"
            :disabled="pendingDoctors.length === 0"
            class="primary-action"
          >
            <span>⚡ 一键发起签署</span>
            <span class="action-count" v-if="pendingDoctors.length">{{ pendingDoctors.length }} 人待处理</span>
          </el-button>
        </template>
        <template v-else>
          <div class="mode-badge">
            <span>📎 线下模式</span>
            <span class="mode-sub">逐人上传已盖章合同</span>
          </div>
        </template>
        <el-button
          class="refresh-btn"
          icon="el-icon-refresh"
          circle
          size="mini"
          @click="loadData"
          :loading="loading"
        ></el-button>
      </div>
    </div>

    <!-- 待处理区（最优先展示） -->
    <div class="msp-group" v-if="pendingDoctors.length">
      <div class="group-header pending">
        <span class="group-dot"></span>
        <span class="group-title">待处理 · {{ pendingDoctors.length }} 人</span>
      </div>
      <div class="doctor-list">
        <div class="doctor-row" v-for="p in pendingDoctors" :key="p.id">
          <div class="doctor-avatar">{{ (p.participantName || '?')[0] }}</div>
          <div class="doctor-info">
            <div class="doctor-name">{{ p.participantName }}</div>
            <div class="doctor-meta">
              <span class="role-tag" :class="'role-' + p.role">{{ roleLabel(p.role) }}</span>
              <span class="fee" v-if="p.laborFee">¥{{ p.laborFee }}</span>
              <span class="hospital" v-if="p.hospitalName">{{ p.hospitalName }}</span>
            </div>
          </div>
          <div class="doctor-action">
            <!-- AUTHORIZED：发起 -->
            <el-button
              v-if="config.sealMode === 'AUTHORIZED'"
              type="primary"
              size="mini"
              :loading="p._loading"
              @click="initiateOne(p)"
              class="action-btn"
            >
              发起签署
            </el-button>
            <!-- OFFLINE：上传 -->
            <el-upload
              v-else
              action="#"
              :http-request="(opts) => uploadStamped(p, opts)"
              :show-file-list="false"
              accept=".doc,.docx,.pdf"
            >
              <el-button size="mini" :loading="p._loading" class="action-btn upload-btn">
                {{ p.manualContractUrl ? '重新上传' : '上传合同' }}
              </el-button>
            </el-upload>
          </div>
        </div>
      </div>
    </div>

    <!-- 签署中区 -->
    <div class="msp-group" v-if="signingDoctors.length">
      <div class="group-header signing">
        <span class="group-dot"></span>
        <span class="group-title">签署中 · {{ signingDoctors.length }} 人</span>
      </div>
      <div class="doctor-list">
        <div class="doctor-row dim" v-for="p in signingDoctors" :key="p.id">
          <div class="doctor-avatar signing">{{ (p.participantName || '?')[0] }}</div>
          <div class="doctor-info">
            <div class="doctor-name">{{ p.participantName }}</div>
            <div class="doctor-meta">
              <span class="role-tag" :class="'role-' + p.role">{{ roleLabel(p.role) }}</span>
              <span class="fee" v-if="p.laborFee">¥{{ p.laborFee }}</span>
            </div>
          </div>
          <div class="doctor-action">
            <span class="status-pulse"></span>
            <el-button type="text" size="mini" @click="copyLink(p)" class="link-btn">
              复制链接
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 已完成区 -->
    <div class="msp-group" v-if="signedDoctors.length">
      <div class="group-header signed">
        <span class="group-dot"></span>
        <span class="group-title">已签署 · {{ signedDoctors.length }} 人</span>
        <span class="group-toggle" @click="showSigned = !showSigned">
          {{ showSigned ? '收起' : '展开' }}
        </span>
      </div>
      <transition name="fade">
        <div class="doctor-list" v-if="showSigned">
          <div class="doctor-row dim" v-for="p in signedDoctors" :key="p.id">
            <div class="doctor-avatar signed">{{ (p.participantName || '?')[0] }}</div>
            <div class="doctor-info">
              <div class="doctor-name">{{ p.participantName }}</div>
              <div class="doctor-meta">
                <span class="role-tag" :class="'role-' + p.role">{{ roleLabel(p.role) }}</span>
                <span class="fee" v-if="p.laborFee">¥{{ p.laborFee }}</span>
              </div>
            </div>
            <div class="doctor-action">
              <el-button type="text" size="mini" @click="viewContract(p)" class="view-btn">
                查看合同 →
              </el-button>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- 批量下载（OFFLINE + SYSTEM_GENERATED） -->
    <div class="msp-download" v-if="config.sealMode === 'OFFLINE' && config.contractSource === 'SYSTEM_GENERATED'">
      <span class="dl-icon">🖨️</span>
      <div class="dl-info">
        <div class="dl-title">批量生成合同模板</div>
        <div class="dl-desc">系统自动填充医生信息，下载后线下盖章，再逐人上传</div>
      </div>
      <el-button size="small" @click="downloadTemplates" :loading="downloading">
        批量下载
      </el-button>
    </div>

    <!-- 空态 -->
    <div class="msp-empty" v-if="total === 0 && !loading">
      <div class="empty-icon">📋</div>
      <div class="empty-text">暂无需要签署合同的医生</div>
      <div class="empty-sub">有劳务费的医生才需要签署合同</div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
const API = 'http://localhost:8080'

export default {
  name: 'MeetingSignPanel',
  props: {
    meetingId: { type: [Number, String], required: true },
    projectId: { type: [Number, String], required: true }
  },
  data() {
    return {
      participants: [],
      config: { sealMode: 'OFFLINE', contractSource: 'PARTY_PROVIDED' },
      loading: false,
      initiating: false,
      downloading: false,
      showSigned: false
    }
  },
  computed: {
    billable() { return this.participants.filter(p => p.laborFee) },
    total() { return this.billable.length },
    signedDoctors() { return this.billable.filter(p => p.fadadaStatus === 'SIGNED') },
    signingDoctors() { return this.billable.filter(p => p.fadadaStatus === 'SIGNING') },
    pendingDoctors() { return this.billable.filter(p => !['SIGNED', 'SIGNING'].includes(p.fadadaStatus)) },
    signedCount() { return this.signedDoctors.length },
    signingCount() { return this.signingDoctors.length },
    pendingCount() { return this.pendingDoctors.length },
    progressArc() {
      if (!this.total) return 0
      return (this.signedCount / this.total) * 150.796
    },
    progressColor() {
      if (!this.total) return 'ring-gray'
      const pct = this.signedCount / this.total
      if (pct >= 1) return 'ring-green'
      if (pct >= 0.5) return 'ring-amber'
      return 'ring-blue'
    }
  },
  mounted() { this.loadData() },
  methods: {
    token() { return localStorage.getItem('token') || sessionStorage.getItem('token') || '' },

    async loadData() {
      this.loading = true
      try {
        const [partRes, configRes] = await Promise.all([
          axios.get(`${API}/api/participant/list/${this.meetingId}`, { headers: { token: this.token() } }),
          axios.get(`${API}/api/contract/config/${this.projectId}`, { headers: { token: this.token() } })
        ])
        this.participants = (partRes.data?.data || []).map(p => ({ ...p, _loading: false }))
        this.config = configRes.data?.data || {}
      } catch (e) { console.error('加载失败', e) }
      finally { this.loading = false }
    },

    async initiateAll() {
      this.initiating = true
      try {
        const res = await axios.post(`${API}/api/contract/initiate/${this.meetingId}`, {}, { headers: { token: this.token() } })
        const r = res.data?.data || {}
        this.$message.success(`✅ 发起成功 ${r.success || 0} 人${r.fail ? `，失败 ${r.fail} 人` : ''}`)
        this.loadData()
      } catch (e) { this.$message.error('发起失败：' + (e.response?.data?.msg || e.message)) }
      finally { this.initiating = false }
    },

    async initiateOne(row) {
      row._loading = true
      try {
        await axios.post(`${API}/api/contract/initiate-one/${row.id}`, {}, { headers: { token: this.token() } })
        row.fadadaStatus = 'SIGNING'
        this.$message.success(`已为 ${row.participantName} 发起签署`)
      } catch (e) { this.$message.error('发起失败：' + (e.response?.data?.msg || e.message)) }
      finally { row._loading = false }
    },

    async uploadStamped(row, { file }) {
      row._loading = true
      const fd = new FormData()
      fd.append('file', file)
      try {
        const res = await axios.post(`${API}/api/contract/upload-stamped/${row.id}`, fd, {
          headers: { token: this.token(), 'Content-Type': 'multipart/form-data' }
        })
        row.signUrl = res.data?.data
        row.fadadaStatus = 'SIGNING'
        this.$message.success(`${row.participantName} 合同已上传并发起签署`)
      } catch (e) { this.$message.error('上传失败：' + (e.response?.data?.msg || e.message)) }
      finally { row._loading = false }
    },

    async downloadTemplates() {
      this.downloading = true
      try {
        const res = await axios.get(`${API}/api/contract/download-templates/${this.meetingId}`, { headers: { token: this.token() } })
        const list = res.data?.data || []
        list.forEach(item => {
          if (item.downloadUrl) {
            const a = document.createElement('a')
            a.href = item.downloadUrl
            a.download = item.doctorName + '_劳务协议.pdf'
            a.click()
          }
        })
        if (!list.length) this.$message.warning('暂无可下载的模板')
      } catch (e) { this.$message.error('下载失败') }
      finally { this.downloading = false }
    },

    copyLink(row) {
      if (!row.signUrl) { this.$message.warning('暂无签署链接'); return }
      navigator.clipboard.writeText(row.signUrl).then(() => this.$message.success('链接已复制'))
    },

    viewContract(row) {
      if (row.contractUrl) window.open(row.contractUrl, '_blank')
      else this.$message.info('合同文件暂未归档')
    },

    roleLabel(r) {
      return { SPEAKER: '讲者', CHAIR: '主席', HOST: '主持', ATTENDEE: '学员' }[r] || r || '参会'
    }
  }
}
</script>

<style scoped>
.msp {
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', sans-serif;
  color: #1a1a2e;
}

/* ===== Summary Bar ===== */
.msp-summary {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 16px 20px;
  background: #f8f9ff;
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid #eef0ff;
}

/* Progress Ring */
.progress-ring-wrap {
  position: relative;
  width: 56px; height: 56px;
  flex-shrink: 0;
}
.progress-ring {
  width: 56px; height: 56px;
  transform: rotate(-90deg);
}
.ring-bg { fill: none; stroke: #e5e7eb; stroke-width: 4; }
.ring-fg { fill: none; stroke-width: 4; stroke-linecap: round; transition: stroke-dasharray 0.5s ease; }
.ring-green { stroke: #22c55e; }
.ring-amber { stroke: #f59e0b; }
.ring-blue  { stroke: #6366f1; }
.ring-gray  { stroke: #d1d5db; }
.ring-label {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  line-height: 1;
}
.ring-num  { font-size: 14px; font-weight: 700; color: #111827; }
.ring-total { font-size: 10px; color: #9ca3af; margin-top: 1px; }

/* Stats */
.summary-stats { display: flex; flex-direction: column; gap: 6px; flex: 1; }
.stat-row { display: flex; align-items: center; gap: 6px; font-size: 12.5px; }
.dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.dot.green { background: #22c55e; }
.dot.amber { background: #f59e0b; }
.dot.gray  { background: #9ca3af; }
.stat-name { flex: 1; color: #6b7280; }
.stat-val { font-weight: 700; min-width: 20px; text-align: right; }
.stat-val.green { color: #16a34a; }
.stat-val.amber { color: #d97706; }
.stat-val.gray  { color: #6b7280; }

/* Action */
.summary-action { display: flex; align-items: center; gap: 8px; }
.primary-action {
  display: flex; flex-direction: column; align-items: center;
  height: auto; padding: 8px 16px; line-height: 1.4;
}
.action-count { font-size: 10.5px; opacity: 0.85; margin-top: 2px; }
.mode-badge {
  display: flex; flex-direction: column;
  font-size: 12px; color: #6b7280;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  min-width: 110px;
}
.mode-sub { font-size: 10.5px; color: #9ca3af; margin-top: 2px; }
.refresh-btn { border-color: transparent !important; background: transparent !important; }

/* ===== Group ===== */
.msp-group { margin-bottom: 12px; }

.group-header {
  display: flex; align-items: center; gap: 7px;
  padding: 6px 4px;
  font-size: 12px; font-weight: 600;
  margin-bottom: 6px;
}
.group-header .group-dot {
  width: 8px; height: 8px; border-radius: 50%;
}
.group-header.pending .group-dot { background: #6366f1; }
.group-header.signing .group-dot { background: #f59e0b; }
.group-header.signed  .group-dot { background: #22c55e; }
.group-header.pending .group-title { color: #4f46e5; }
.group-header.signing .group-title { color: #d97706; }
.group-header.signed  .group-title { color: #16a34a; }

.group-toggle {
  margin-left: auto;
  font-size: 11px; font-weight: 400;
  color: #9ca3af; cursor: pointer;
}
.group-toggle:hover { color: #6366f1; }

/* ===== Doctor Row ===== */
.doctor-list { display: flex; flex-direction: column; gap: 4px; }

.doctor-row {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 9px;
  transition: border-color 0.1s;
}
.doctor-row:hover { border-color: #e0e7ff; }
.doctor-row.dim { opacity: 0.8; }

.doctor-avatar {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff; font-size: 13px; font-weight: 600;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.doctor-avatar.signing { background: linear-gradient(135deg, #f59e0b, #ec8c04); }
.doctor-avatar.signed  { background: linear-gradient(135deg, #22c55e, #16a34a); }

.doctor-info { flex: 1; min-width: 0; }
.doctor-name { font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 3px; }
.doctor-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

.role-tag {
  font-size: 10.5px; padding: 1px 6px; border-radius: 100px;
}
.role-SPEAKER { background: #ede9fe; color: #6d28d9; }
.role-CHAIR   { background: #d1fae5; color: #065f46; }
.role-HOST    { background: #fef3c7; color: #92400e; }
.role-ATTENDEE { background: #f3f4f6; color: #6b7280; }

.fee { font-size: 12px; font-weight: 600; color: #059669; }
.hospital { font-size: 11.5px; color: #9ca3af; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px; }

.doctor-action { flex-shrink: 0; display: flex; align-items: center; gap: 6px; }

.action-btn { border-radius: 6px !important; }
.upload-btn { background: #f5f3ff !important; border-color: #c4b5fd !important; color: #6d28d9 !important; }
.link-btn { color: #6366f1 !important; font-size: 12px !important; }
.view-btn { color: #16a34a !important; font-size: 12px !important; }

/* Pulse animation */
.status-pulse {
  display: inline-block;
  width: 8px; height: 8px;
  background: #f59e0b;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.85); }
}

/* ===== Download Banner ===== */
.msp-download {
  display: flex; align-items: center; gap: 12px;
  margin-top: 16px; padding: 12px 16px;
  background: #fafafa;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
}
.dl-icon { font-size: 22px; }
.dl-info { flex: 1; }
.dl-title { font-size: 13px; font-weight: 600; color: #374151; }
.dl-desc  { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }

/* ===== Empty ===== */
.msp-empty { text-align: center; padding: 40px 20px; }
.empty-icon { font-size: 36px; margin-bottom: 10px; }
.empty-text { font-size: 14px; font-weight: 500; color: #6b7280; }
.empty-sub  { font-size: 12px; color: #9ca3af; margin-top: 4px; }

/* ===== Fade ===== */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
